import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import { buildAuthHeaders } from '../../services/authStorage';
import wsService from '../../services/websocket';
import { resolveSafeAppPath } from '../../utils/safeAppPath';
import './NotificationBell.css';

// M004-F0666 (c) / T-M004-0122 (WebUI #53): Layout renders this bell twice
// (mobile header + sidebar). Both instances share ONE `/notifications?limit=10`
// read: a concurrent mount joins the in-flight request and a body younger than
// SHARED_READ_TTL_MS is reused. Live updates still arrive over the websocket.
export const SHARED_READ_TTL_MS = 30_000;
let sharedRead = null; // { key, at, promise }

export const resetSharedNotificationRead = () => { sharedRead = null; };

const readNotificationsShared = () => {
  const headers = buildAuthHeaders();
  const key = `${config.apiBaseUrl}|${headers?.Authorization || ''}`;
  const now = Date.now();
  if (sharedRead && sharedRead.key === key && now - sharedRead.at < SHARED_READ_TTL_MS) return sharedRead.promise;
  const promise = fetch(`${config.apiBaseUrl}/notifications?limit=10`, { headers })
    .then(async (response) => (response.ok ? response.json() : null));
  sharedRead = { key, at: now, promise };
  // A failed read is not cached: the next mount asks again.
  promise.catch(() => { if (sharedRead?.promise === promise) sharedRead = null; });
  return promise;
};

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const dropdownRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const data = await readNotificationsShared();
      if (data) {
        setNotifications(data.data || []);
        setUnreadCount(data.meta?.unread || 0);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  // Initial fetch and WebSocket subscription
  useEffect(() => {
    fetchNotifications();
    
    // Connect to WebSocket
    wsService.connect();

    // M004-F0079: Subscribe to real-time notifications
    const unsubNewNotification = wsService.subscribe('notification:new', (data) => {
      console.log('New notification via WebSocket:', data);
      // Add to top of list
      setNotifications(prev => [data.notification, ...prev.slice(0, 9)]);
      setUnreadCount(prev => prev + 1);
      setToast({
        title: data.notification?.title || 'New notification',
        text: data.notification?.text || '',
        category: data.notification?.push_category || data.notification?.type || 'notification',
        url: data.notification?.url || '/app/notifications',
      });
    });

    const unsubSecurityEvent = wsService.subscribe('security:event', (event) => {
      setToast({
        title: event.title || 'Security event',
        text: event.message || event.text || event.event_type || '',
        category: 'security',
        url: event.event_id ? `/app/account/security/events/${event.event_id}` : '/app/account/security/events',
      });
      setUnreadCount(prev => prev + 1);
    });

    // Cleanup
    return () => {
      unsubNewNotification();
      unsubSecurityEvent();
    };
  }, []);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }

    const timeout = setTimeout(() => setToast(null), 7000);
    return () => clearTimeout(timeout);
  }, [toast]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      sharedRead = null; // a write invalidates the shared read
      await fetch(`${config.apiBaseUrl}/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: buildAuthHeaders()
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    setLoading(true);
    try {
      sharedRead = null; // a write invalidates the shared read
      await fetch(`${config.apiBaseUrl}/notifications/mark-all-read`, {
        method: 'POST',
        headers: buildAuthHeaders()
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.url) {
      // T-NOTIF-012: only a same-origin application path may navigate. Anything
      // else (javascript:, data:, external, //host) opens nothing; the item has
      // already been marked read above.
      const safePath = resolveSafeAppPath(notification.url);
      if (safePath) {
        navigate(safePath);
      }
    }
    setIsOpen(false);
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get icon for notification type
  const getNotificationIcon = (type) => {
    if (String(type || '').startsWith('network_')) {
      return '🛜';
    }

    switch (type) {
      case 'booking_request': return '📅';
      case 'booking_approved': return '✅';
      case 'booking_rejected': return '❌';
      case 'user_onboarding': return '👤';
      case 'document_expiring': return '📄';
      case 'warning': return '⚠️';
      case 'error': return '🚨';
      case 'success': return '🎉';
      default: return '🔔';
    }
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      {toast && (
        <button
          type="button"
          className={`notification-toast notification-toast-${toast.category}`}
          onClick={() => {
            setToast(null);
            navigate(toast.url);
          }}
        >
          <strong>{toast.title}</strong>
          {toast.text && <span>{toast.text}</span>}
        </button>
      )}

      <button 
        className="notification-bell-button" 
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <span className="bell-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button 
                className="mark-all-read-btn"
                onClick={markAllAsRead}
                disabled={loading}
              >
                {loading ? '...' : 'Mark all read'}
              </button>
            )}
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="no-notifications">
                <span className="no-notifications-icon">🔕</span>
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className={`notification-item ${notification.is_read ? 'read' : 'unread'}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="notification-content">
                    <div className="notification-title">{notification.title}</div>
                    <div className="notification-text">{notification.text}</div>
                    <div className="notification-time">
                      {formatTimeAgo(notification.created_at)}
                    </div>
                  </div>
                  {!notification.is_read && <span className="unread-dot"></span>}
                </div>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-footer">
              <a href="/app/notifications" className="view-all-link">
                View all notifications
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
